import { functions } from '@/firebase'
import { traceHttpsCallable } from '@/utils/sentry'
import type {
  GenerateTokenRequest,
  GenerateTokenResponse,
  ZoomUserReduced,
  ZoomUsersResponse,
  RegisterAndGetJoinUrlRequest,
  RegisterAndGetJoinUrlResponse,
  QueueRegistrationRequest,
  QueueRegistrationResponse,
} from '@/models/zoom'

// Types for recording upload functionality
export interface GenerateRecordingUploadUrlRequest {
  classInstanceId: string
  fileName: string
  contentType: string
  fileSize: number
}

export interface GenerateRecordingUploadUrlResponse {
  recordingId: string
  signedUrl: string
  firebaseStoragePath: string
  expiresAt: Date
}

export interface UpdateRecordingStatusAfterUploadRequest {
  recordingId: string
  firebaseStoragePath?: string | null
  errorMessage?: string | null
}

export interface ManualClassInstanceWebinarSyncRequest {
  classInstanceId: string
  force: boolean
}

export interface ManualClassInstanceWebinarSyncResponse {
  success: boolean
  message: string
  classInstanceId: string
  force: boolean
}

export interface TriggerWebinarReportRequest {
  webinarId: string
  classInstanceId: string
}

export interface TriggerWebinarReportResponse {
  success: boolean
  message: string
  webinarId: string
  classInstanceId: string
}

export interface EnableRegistrationForExistingMeetingsResponse {
  success: boolean
  updatedCount: number
  errors: Array<{ classInstanceId: string; error: string }>
}

export const Zoom = {
  generateToken: traceHttpsCallable<GenerateTokenRequest, GenerateTokenResponse>(
    functions,
    'zoom_generateToken',
  ),
  registerAndGetJoinUrl: traceHttpsCallable<
    RegisterAndGetJoinUrlRequest,
    RegisterAndGetJoinUrlResponse
  >(functions, 'zoom_registerAndGetJoinUrl'),
  queueRegistration: traceHttpsCallable<QueueRegistrationRequest, QueueRegistrationResponse>(
    functions,
    'zoom_queueRegistration',
  ),
  getUsers: traceHttpsCallable<object, ZoomUsersResponse>(functions, 'zoom_getUsers'),
  getUserById: traceHttpsCallable<{ zoomUserId: string }, ZoomUserReduced>(
    functions,
    'zoom_getUserById',
  ),
  generateRecordingUploadUrl: traceHttpsCallable<
    GenerateRecordingUploadUrlRequest,
    GenerateRecordingUploadUrlResponse
  >(functions, 'zoom_generateRecordingUploadUrl'),
  updateRecordingStatusAfterUpload: traceHttpsCallable<
    UpdateRecordingStatusAfterUploadRequest,
    void
  >(functions, 'zoom_updateRecordingStatusAfterUpload'),
  manualClassInstanceWebinarSync: traceHttpsCallable<
    ManualClassInstanceWebinarSyncRequest,
    ManualClassInstanceWebinarSyncResponse
  >(functions, 'zoom_manualClassInstanceWebinarSync'),
  triggerWebinarReport: traceHttpsCallable<
    TriggerWebinarReportRequest,
    TriggerWebinarReportResponse
  >(functions, 'zoom_triggerWebinarReport'),
  enableRegistrationForExistingMeetings: traceHttpsCallable<
    object,
    EnableRegistrationForExistingMeetingsResponse
  >(functions, 'zoom_enableRegistrationForExistingMeetings'),
}
